import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ListChecks, Calculator, Clock, HeartHandshake, Loader2, FileDown, Mail, Check, X } from "lucide-react";
import { jsPDF } from "jspdf";
import { streamPost, API } from "@/lib/stream";

const TABS = [
  { id: "onboarding", label: "Onboarding", icon: ListChecks, color: "#FF5722", tag: "AI 30/60/90 checklists" },
  { id: "payroll", label: "Payroll", icon: Calculator, color: "#2563EB", tag: "Run cost estimator" },
  { id: "time", label: "Time", icon: Clock, color: "#F59E0B", tag: "PTO accrual tracker" },
  { id: "benefits", label: "Benefits", icon: HeartHandshake, color: "#EC4899", tag: "Plan cost builder" },
];

const inputCls =
  "w-full bg-white rounded-2xl border-2 border-slate-900/10 px-4 py-3 text-sm font-medium outline-none focus:border-[#FF5722] transition-colors placeholder:text-slate-400";

const fmtUsd = (n) => n.toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });

function pdfHeader(doc, title, subtitle, accent) {
  const W = 612;
  doc.setFillColor(15, 23, 42);
  doc.rect(0, 0, W, 96, "F");
  doc.setFillColor(...accent);
  doc.rect(0, 96, W, 6, "F");
  doc.setTextColor(255, 255, 255);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(22);
  doc.text(title, 48, 44);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.setTextColor(203, 213, 225);
  doc.text(subtitle, 48, 66);
  return W;
}

function pdfDisclaimer(doc) {
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8.5);
  doc.setTextColor(148, 163, 184);
  doc.text("Estimates for planning only — confirm final figures with your provider before decisions.", 48, 780);
}

function OnboardingTool() {
  const [role, setRole] = useState("");
  const [propType, setPropType] = useState("Manufactured housing");
  const [output, setOutput] = useState("");
  const [busy, setBusy] = useState(false);

  const downloadPdf = () => {
    const doc = new jsPDF({ unit: "pt", format: "letter" });
    pdfHeader(
      doc,
      "30/60/90 Onboarding Checklist",
      `Opera360 AI  ·  ${role || "Community Manager"}  ·  ${propType}  ·  drafted by Claude`,
      [255, 87, 34]
    );
    doc.setFont("courier", "normal");
    doc.setFontSize(9.5);
    doc.setTextColor(51, 65, 85);
    const lines = doc.splitTextToSize(output, 500);
    let y = 130;
    lines.forEach((ln) => {
      if (y > 740) {
        doc.addPage();
        y = 56;
      }
      doc.text(ln, 48, y);
      y += 13;
    });
    pdfDisclaimer(doc);
    doc.save(`onboarding-checklist-${(role || "role").toLowerCase().replace(/[^a-z0-9]+/g, "-")}.pdf`);
  };

  const generate = async () => {
    if (busy) return;
    setBusy(true);
    setOutput("");
    try {
      await streamPost("/onboarding", { role, property_type: propType }, (d) => setOutput((o) => o + d));
    } catch {
      setOutput("Checklist failed to generate — please try again.");
    }
    setBusy(false);
  };

  return (
    <div className="grid lg:grid-cols-2 gap-6">
      <div className="space-y-4">
        <input data-testid="ops-onboarding-role-input" value={role} onChange={(e) => setRole(e.target.value)} placeholder="Role, e.g. Leasing Agent" className={inputCls} />
        <select value={propType} onChange={(e) => setPropType(e.target.value)} className={inputCls}>
          {["Manufactured housing", "Multifamily", "RV resort", "Single-family", "Mixed portfolio"].map((p) => <option key={p}>{p}</option>)}
        </select>
        <button data-testid="ops-onboarding-generate-button" onClick={generate} disabled={busy} className="w-full inline-flex items-center justify-center gap-2 px-6 py-4 rounded-full bg-[#FF5722] text-white font-bold text-sm hover:bg-[#0F172A] transition-colors duration-300 disabled:opacity-60">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ListChecks className="w-4 h-4" />}
          {busy ? "Claude is building…" : "Generate 30/60/90 checklist"}
        </button>
        <p className="text-xs font-semibold text-slate-400">Drafted by Claude · compliance training, systems access, shadowing and sign-off checkpoints.</p>
      </div>
      <div className="rounded-2xl bg-[#0F172A] p-5 min-h-[16rem] flex flex-col">
        <div className="flex items-center justify-between pb-3 border-b border-white/10 mb-3">
          <span className="text-[10px] font-bold tracking-widest uppercase text-slate-400">Checklist preview</span>
          <button
            data-testid="ops-onboarding-pdf-button"
            onClick={downloadPdf}
            disabled={!output || busy}
            className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full bg-[#FF5722] text-xs font-bold text-white hover:bg-[#10B981] transition-colors disabled:opacity-40"
          >
            <FileDown className="w-3.5 h-3.5" /> PDF
          </button>
        </div>
        <div data-testid="ops-onboarding-output" className="doc-preview doc-scroll max-h-72 overflow-y-auto text-xs leading-relaxed text-slate-300 flex-1">
          {output || <span className="text-slate-500">Your checklist streams here…</span>}
          {busy && <span className="inline-block w-2 h-3.5 bg-[#FF5722] animate-pulse ml-0.5 align-middle" />}
        </div>
      </div>
    </div>
  );
}

function PayrollTool() {
  const [employees, setEmployees] = useState(12);
  const [salary, setSalary] = useState(52000);
  const [stateTax, setStateTax] = useState(4);
  const FICA = 0.0765;
  const perYearGross = employees * salary;
  const employerTax = perYearGross * (FICA + stateTax / 100);
  const perRun = (perYearGross + employerTax) / 26;

  const exportPdf = () => {
    const doc = new jsPDF({ unit: "pt", format: "letter" });
    const W = pdfHeader(
      doc,
      "Payroll Run Estimate",
      `Opera360 AI  ·  ${employees} team members  ·  ${new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })}`,
      [37, 99, 235]
    );
    let y = 140;
    doc.setFontSize(11);
    doc.setTextColor(100, 116, 139);
    doc.setFont("helvetica", "bold");
    doc.text("ASSUMPTIONS", 48, y);
    y += 22;
    doc.setFont("helvetica", "normal");
    [
      ["Team members", String(employees)],
      ["Average salary", `${fmtUsd(salary)} / year`],
      ["Employer FICA", "7.65%"],
      ["State employer tax", `${stateTax}%`],
      ["Pay frequency", "Bi-weekly (26 runs / year)"],
    ].forEach(([k, v]) => {
      doc.setTextColor(15, 23, 42);
      doc.setFontSize(12);
      doc.text(k, 48, y);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(37, 99, 235);
      doc.text(v, W - 48, y, { align: "right" });
      doc.setFont("helvetica", "normal");
      doc.setDrawColor(226, 232, 240);
      doc.line(48, y + 10, W - 48, y + 10);
      y += 34;
    });
    y += 16;
    doc.setFillColor(244, 244, 240);
    doc.roundedRect(48, y, W - 96, 108, 12, 12, "F");
    doc.setTextColor(100, 116, 139);
    doc.setFontSize(10);
    doc.text("PER PAYROLL RUN", 72, y + 30);
    doc.text("MONTHLY PAYROLL", 300, y + 30);
    doc.text("EMPLOYER TAXES / YEAR", 72, y + 76);
    doc.text("TOTAL ANNUAL COST", 300, y + 76);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(16);
    doc.setTextColor(37, 99, 235);
    doc.text(fmtUsd(perRun), 72, y + 48);
    doc.setTextColor(15, 23, 42);
    doc.text(fmtUsd((perYearGross + employerTax) / 12), 300, y + 48);
    doc.setTextColor(236, 72, 153);
    doc.text(fmtUsd(employerTax), 72, y + 94);
    doc.setTextColor(255, 87, 34);
    doc.text(fmtUsd(perYearGross + employerTax), 300, y + 94);
    pdfDisclaimer(doc);
    doc.save("payroll-run-estimate.pdf");
  };

  return (
    <div className="grid lg:grid-cols-2 gap-6 items-start">
      <div className="space-y-5">
        {[
          { label: "Team members", value: employees, set: setEmployees, min: 1, max: 250, step: 1, testid: "ops-payroll-employees-input" },
          { label: "Average salary", value: salary, set: setSalary, min: 30000, max: 150000, step: 1000, testid: "ops-payroll-salary-input" },
          { label: "State employer tax %", value: stateTax, set: setStateTax, min: 0, max: 12, step: 0.5, testid: "ops-payroll-tax-input" },
        ].map((s) => (
          <div key={s.label}>
            <div className="flex justify-between text-sm font-bold mb-1.5">
              <span className="text-slate-600">{s.label}</span>
              <span className="text-[#2563EB]">{s.label.includes("salary") ? fmtUsd(s.value) : s.label.includes("%") ? `${s.value}%` : s.value}</span>
            </div>
            <input data-testid={s.testid} type="range" min={s.min} max={s.max} step={s.step} value={s.value} onChange={(e) => s.set(Number(e.target.value))} className="w-full accent-[#2563EB]" />
          </div>
        ))}
        <p className="text-xs font-semibold text-slate-400">Includes 7.65% FICA + your state estimate · bi-weekly runs (26/year).</p>
        <button
          data-testid="ops-payroll-pdf-button"
          onClick={exportPdf}
          className="w-full inline-flex items-center justify-center gap-2 px-6 py-4 rounded-full bg-[#2563EB] text-white font-bold text-sm hover:bg-[#0F172A] transition-colors duration-300"
        >
          <FileDown className="w-4 h-4" /> Download owner-review PDF
        </button>
      </div>
      <div className="grid grid-cols-2 gap-3">
        {[
          { l: "Per payroll run", v: fmtUsd(perRun), c: "#2563EB", testid: "ops-payroll-per-run" },
          { l: "Monthly payroll", v: fmtUsd((perYearGross + employerTax) / 12), c: "#0F172A" },
          { l: "Employer taxes / yr", v: fmtUsd(employerTax), c: "#EC4899" },
          { l: "Total annual cost", v: fmtUsd(perYearGross + employerTax), c: "#FF5722", testid: "ops-payroll-annual" },
        ].map((t, i) => (
          <motion.div key={t.l} initial={{ opacity: 0, scale: 0.92 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: i * 0.06 }} className="rounded-2xl border-2 border-slate-900/5 bg-white p-5 shadow-sm">
            <div className="text-[10px] font-bold uppercase tracking-widest text-slate-400">{t.l}</div>
            <div data-testid={t.testid} className="font-display font-black text-xl sm:text-2xl mt-1" style={{ color: t.c }}>{t.v}</div>
          </motion.div>
        ))}
      </div>
    </div>
  );
}

function TimeTool() {
  const [accrual, setAccrual] = useState(3.08);
  const [tenure, setTenure] = useState(8);
  const [taken, setTaken] = useState(24);
  const accrued = accrual * tenure * 2.17;
  const balance = Math.max(0, accrued - taken);
  const pct = Math.min(100, (balance / 120) * 100);

  const exportPdf = () => {
    const doc = new jsPDF({ unit: "pt", format: "letter" });
    const W = pdfHeader(
      doc,
      "PTO Balance Summary",
      `Opera360 AI  ·  Time & attendance  ·  ${new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })}`,
      [245, 158, 11]
    );
    let y = 140;
    doc.setFontSize(11);
    doc.setTextColor(100, 116, 139);
    doc.setFont("helvetica", "bold");
    doc.text("ACCRUAL INPUTS", 48, y);
    y += 22;
    doc.setFont("helvetica", "normal");
    [
      ["Accrual per pay period", `${accrual} hrs`],
      ["Tenure", `${tenure} months`],
      ["PTO already taken", `${taken} hrs`],
      ["Pay periods", "Bi-weekly (26 / year)"],
      ["Annual cap shown", "120 hrs"],
    ].forEach(([k, v]) => {
      doc.setTextColor(15, 23, 42);
      doc.setFontSize(12);
      doc.text(k, 48, y);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(245, 158, 11);
      doc.text(v, W - 48, y, { align: "right" });
      doc.setFont("helvetica", "normal");
      doc.setDrawColor(226, 232, 240);
      doc.line(48, y + 10, W - 48, y + 10);
      y += 34;
    });
    y += 16;
    doc.setFillColor(244, 244, 240);
    doc.roundedRect(48, y, W - 96, 108, 12, 12, "F");
    doc.setTextColor(100, 116, 139);
    doc.setFontSize(10);
    doc.text("TOTAL ACCRUED", 72, y + 30);
    doc.text("ALREADY TAKEN", 300, y + 30);
    doc.text("AVAILABLE BALANCE", 72, y + 76);
    doc.text("% OF ANNUAL CAP", 300, y + 76);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(16);
    doc.setTextColor(15, 23, 42);
    doc.text(`${accrued.toFixed(1)} hrs`, 72, y + 48);
    doc.text(`${taken} hrs`, 300, y + 48);
    doc.setTextColor(245, 158, 11);
    doc.text(`${balance.toFixed(1)} hrs`, 72, y + 94);
    doc.setTextColor(15, 23, 42);
    doc.text(`${Math.round(pct)}%`, 300, y + 94);
    pdfDisclaimer(doc);
    doc.save("pto-balance-summary.pdf");
  };

  return (
    <div className="grid lg:grid-cols-2 gap-6 items-start">
      <div className="space-y-4">
        <div className="grid grid-cols-3 gap-3">
          <div>
            <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Accrue / period</label>
            <input data-testid="ops-time-accrual-input" type="number" step="0.01" value={accrual} onChange={(e) => setAccrual(Number(e.target.value) || 0)} className={`${inputCls} mt-1.5`} />
          </div>
          <div>
            <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Tenure (mo)</label>
            <input data-testid="ops-time-tenure-input" type="number" value={tenure} onChange={(e) => setTenure(Number(e.target.value) || 0)} className={`${inputCls} mt-1.5`} />
          </div>
          <div>
            <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Taken (hrs)</label>
            <input data-testid="ops-time-taken-input" type="number" value={taken} onChange={(e) => setTaken(Number(e.target.value) || 0)} className={`${inputCls} mt-1.5`} />
          </div>
        </div>
        <p className="text-xs font-semibold text-slate-400">Bi-weekly accrual periods · 120 hr annual cap shown.</p>
        <button
          data-testid="ops-time-pdf-button"
          onClick={exportPdf}
          className="w-full inline-flex items-center justify-center gap-2 px-6 py-4 rounded-full bg-[#F59E0B] text-white font-bold text-sm hover:bg-[#0F172A] transition-colors duration-300"
        >
          <FileDown className="w-4 h-4" /> Download PTO summary PDF
        </button>
      </div>
      <div className="rounded-2xl bg-[#0F172A] p-6">
        <div className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Available PTO balance</div>
        <div data-testid="ops-time-balance" className="font-display font-black text-4xl text-[#F59E0B] mt-2">{balance.toFixed(1)} hrs</div>
        <div className="text-xs font-bold text-slate-400 mt-1">{accrued.toFixed(1)} accrued · {taken} taken</div>
        <div className="h-3 rounded-full bg-white/10 mt-4 overflow-hidden">
          <motion.div animate={{ width: `${pct}%` }} transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }} className="h-full rounded-full bg-[#F59E0B]" />
        </div>
      </div>
    </div>
  );
}

const PERKS = [
  { id: "health", label: "Health insurance", cost: 450 },
  { id: "dental", label: "Dental & vision", cost: 55 },
  { id: "401k", label: "401(k) 4% match", cost: 173 },
  { id: "stipend", label: "Wellness stipend", cost: 75 },
  { id: "life", label: "Life & disability", cost: 38 },
  { id: "learning", label: "Learning budget", cost: 60 },
];

function BenefitsTool() {
  const [selected, setSelected] = useState(["health", "dental"]);
  const [headcount, setHeadcount] = useState(12);
  const [showEmail, setShowEmail] = useState(false);
  const [senderName, setSenderName] = useState("");
  const [senderCompany, setSenderCompany] = useState("");
  const [emailState, setEmailState] = useState("idle");
  const toggle = (id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const chosen = PERKS.filter((p) => selected.includes(p.id));
  const perEmployee = chosen.reduce((a, p) => a + p.cost, 0);

  const exportPdf = () => {
    const doc = new jsPDF({ unit: "pt", format: "letter" });
    const W = pdfHeader(
      doc,
      "Benefits Plan Summary",
      `Opera360 AI  ·  ${headcount} team members  ·  ${new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })}`,
      [236, 72, 153]
    );
    let y = 140;
    doc.setFontSize(11);
    doc.setTextColor(100, 116, 139);
    doc.setFont("helvetica", "bold");
    doc.text("INCLUDED IN YOUR PLAN", 48, y);
    y += 22;
    doc.setFont("helvetica", "normal");
    chosen.forEach((p) => {
      doc.setTextColor(15, 23, 42);
      doc.setFontSize(12);
      doc.text(p.label, 48, y);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(236, 72, 153);
      doc.text(`${fmtUsd(p.cost)} / mo`, W - 48, y, { align: "right" });
      doc.setFont("helvetica", "normal");
      doc.setDrawColor(226, 232, 240);
      doc.line(48, y + 10, W - 48, y + 10);
      y += 34;
    });
    y += 16;
    doc.setFillColor(244, 244, 240);
    doc.roundedRect(48, y, W - 96, 108, 12, 12, "F");
    doc.setTextColor(100, 116, 139);
    doc.setFontSize(10);
    doc.text("COST PER EMPLOYEE / MONTH", 72, y + 30);
    doc.text("TOTAL / MONTH", 300, y + 30);
    doc.text("TOTAL / YEAR", 72, y + 76);
    doc.text("PERKS INCLUDED", 300, y + 76);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(16);
    doc.setTextColor(15, 23, 42);
    doc.text(fmtUsd(perEmployee), 72, y + 48);
    doc.setTextColor(236, 72, 153);
    doc.text(fmtUsd(perEmployee * headcount), 300, y + 48);
    doc.setTextColor(15, 23, 42);
    doc.text(fmtUsd(perEmployee * headcount * 12), 72, y + 94);
    doc.text(`${chosen.length} of ${PERKS.length}`, 300, y + 94);
    pdfDisclaimer(doc);
    doc.save("benefits-plan-summary.pdf");
  };

  const emailTeam = async () => {
    if (emailState === "sending") return;
    setEmailState("sending");
    try {
      const res = await fetch(`${API}/plan/email-team`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          sender_name: senderName,
          company: senderCompany,
          headcount,
          perks: chosen.map((p) => `${p.label} — ${fmtUsd(p.cost)}/mo per person`),
          per_employee: perEmployee,
          total_monthly: perEmployee * headcount,
        }),
      });
      setEmailState(res.ok ? "sent" : "error");
    } catch {
      setEmailState("error");
    }
    if (emailState !== "error") setTimeout(() => setEmailState("idle"), 4000);
  };

  return (
    <div className="grid lg:grid-cols-2 gap-6 items-start">
      <div>
        <div className="grid grid-cols-2 gap-2.5">
          {PERKS.map((p, i) => (
            <button
              key={p.id}
              data-testid={`ops-benefits-perk-${i}`}
              onClick={() => toggle(p.id)}
              className={`rounded-2xl border-2 px-4 py-3.5 text-left transition-all duration-200 ${
                selected.includes(p.id) ? "border-[#EC4899] bg-[#EC4899]/5 shadow-sm" : "border-slate-900/10 bg-white hover:border-[#EC4899]/50"
              }`}
            >
              <div className="text-sm font-bold text-slate-800">{p.label}</div>
              <div className="text-xs font-bold text-[#EC4899]">{fmtUsd(p.cost)}/mo per person</div>
            </button>
          ))}
        </div>
        <div className="mt-4">
          <div className="flex justify-between text-sm font-bold mb-1.5"><span className="text-slate-600">Headcount</span><span className="text-[#EC4899]">{headcount}</span></div>
          <input data-testid="ops-benefits-headcount-input" type="range" min="1" max="250" value={headcount} onChange={(e) => setHeadcount(Number(e.target.value))} className="w-full accent-[#EC4899]" />
        </div>
        <button
          data-testid="ops-benefits-pdf-button"
          onClick={exportPdf}
          disabled={chosen.length === 0}
          className="mt-4 w-full inline-flex items-center justify-center gap-2 px-6 py-4 rounded-full bg-[#EC4899] text-white font-bold text-sm hover:bg-[#0F172A] transition-colors duration-300 disabled:opacity-50"
        >
          <FileDown className="w-4 h-4" /> Download shareable PDF
        </button>
        <button
          data-testid="ops-benefits-email-toggle"
          onClick={() => setShowEmail(!showEmail)}
          disabled={chosen.length === 0}
          className="mt-2.5 w-full inline-flex items-center justify-center gap-2 px-6 py-4 rounded-full border-2 border-[#EC4899]/40 text-[#EC4899] font-bold text-sm hover:bg-[#EC4899] hover:text-white hover:border-[#EC4899] transition-colors duration-300 disabled:opacity-50"
        >
          <Mail className="w-4 h-4" /> Email this plan to the Opera360 team
        </button>
        <AnimatePresence>
          {showEmail && (
            <motion.div
              data-testid="ops-benefits-email-form"
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.3 }}
              className="overflow-hidden"
            >
              <div className="mt-3 rounded-2xl border-2 border-[#EC4899]/20 bg-white p-4 space-y-2.5">
                <div className="grid grid-cols-2 gap-2.5">
                  <input data-testid="ops-benefits-email-name" value={senderName} onChange={(e) => setSenderName(e.target.value)} placeholder="Your name" className={inputCls} />
                  <input data-testid="ops-benefits-email-company" value={senderCompany} onChange={(e) => setSenderCompany(e.target.value)} placeholder="Company" className={inputCls} />
                </div>
                <button
                  data-testid="ops-benefits-email-submit"
                  onClick={emailTeam}
                  disabled={emailState === "sending" || chosen.length === 0}
                  className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-full bg-[#0F172A] text-white text-xs font-bold hover:bg-[#EC4899] transition-colors disabled:opacity-50"
                >
                  {emailState === "sending" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : emailState === "sent" ? <Check className="w-3.5 h-3.5" /> : <Mail className="w-3.5 h-3.5" />}
                  {emailState === "sending" ? "Sending…" : emailState === "sent" ? "Plan sent to the team inbox" : "Send plan summary"}
                </button>
                {emailState === "error" && <div data-testid="ops-benefits-email-error" className="text-xs font-bold text-red-600">Send failed — please try again in a moment.</div>}
                <p className="text-[10px] font-semibold text-slate-400">For anti-spam protection, plans go to the Opera360 team inbox rather than a typed-in address.</p>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
        <p className="text-xs font-semibold text-slate-400 mt-2">A branded one-pager with your plan, per-employee cost and totals — ready to share with the team.</p>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div className="rounded-2xl bg-[#0F172A] p-5 col-span-2">
          <div className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Total benefits cost / month</div>
          <div data-testid="ops-benefits-total" className="font-display font-black text-3xl text-[#EC4899] mt-1">{fmtUsd(perEmployee * headcount)}</div>
        </div>
        <div className="rounded-2xl border-2 border-slate-900/5 bg-white p-5 shadow-sm">
          <div className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Per employee / mo</div>
          <div className="font-display font-black text-2xl mt-1 text-slate-900">{fmtUsd(perEmployee)}</div>
        </div>
        <div className="rounded-2xl border-2 border-slate-900/5 bg-white p-5 shadow-sm">
          <div className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Perks selected</div>
          <div className="font-display font-black text-2xl mt-1 text-slate-900">{selected.length} of {PERKS.length}</div>
        </div>
      </div>
    </div>
  );
}

const TOOLS = { onboarding: OnboardingTool, payroll: PayrollTool, time: TimeTool, benefits: BenefitsTool };

export default function HROpsSuite() {
  const [active, setActive] = useState("onboarding");
  const Active = TOOLS[active];
  const current = TABS.find((t) => t.id === active);

  return (
    <section id="hr-suite" className="py-24 sm:py-32">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="max-w-2xl"
        >
          <div className="text-xs uppercase tracking-[0.2em] font-bold text-[#EC4899]">The HR Ops Suite</div>
          <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tight leading-tight mt-3">
            Onboarding, payroll, time & benefits — handled.
          </h2>
          <p className="text-lg text-slate-700 leading-relaxed mt-4">
            Four working tools your people team can use right now. Try them — nothing leaves your browser except the AI checklist.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
          className="mt-12"
        >
          <div className="flex flex-wrap gap-2" role="tablist" aria-label="HR ops tools">
            {TABS.map((t) => {
              const Icon = t.icon;
              const isActive = t.id === active;
              return (
                <button
                  key={t.id}
                  data-testid={`ops-tab-${t.id}`}
                  role="tab"
                  aria-selected={isActive}
                  onClick={() => setActive(t.id)}
                  className={`inline-flex items-center gap-2 px-5 py-3 rounded-full text-sm font-bold border-2 transition-all duration-300 ${
                    isActive ? "text-white border-transparent shadow-lg" : "border-slate-900/10 text-slate-600 hover:border-slate-400 bg-white"
                  }`}
                  style={isActive ? { background: t.color } : undefined}
                >
                  <Icon className="w-4 h-4" /> {t.label}
                </button>
              );
            })}
          </div>

          <div className="mt-6 rounded-3xl border-2 border-slate-900/5 bg-[#FBFBF9] p-7 sm:p-10 shadow-[0_12px_40px_-15px_rgba(15,23,42,0.08)] relative overflow-hidden">
            <div className="absolute top-0 left-0 right-0 h-1.5" style={{ background: current.color }} />
            <div className="flex items-center justify-between mb-7">
              <div>
                <div className="font-display font-black text-2xl" style={{ color: current.color }}>{current.label}</div>
                <div className="text-sm font-bold text-slate-500">{current.tag}</div>
              </div>
              <span className="text-[10px] font-bold uppercase tracking-widest text-slate-400 bg-white border border-slate-200 rounded-full px-3 py-1.5">
                Live tool · estimates
              </span>
            </div>
            <AnimatePresence mode="wait">
              <motion.div
                key={active}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
              >
                <Active />
              </motion.div>
            </AnimatePresence>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
